import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import ImageLibrary from "./ImageLibrary";
import RichEditor from "../../components/RichEditor";
import ImageEditor from "../../components/ImageEditor";

// Repeater: visual add/remove rows for list fields (strings or objects), no JSON typing.
function RepeaterField({ value, onChange, item, itemLabel, addLabel, testid }) {
  const rows = Array.isArray(value) ? value : [];
  const isString = item === "string";
  const update = (i, v) => { const n = [...rows]; n[i] = v; onChange(n); };
  const updateField = (i, name, v) => { const n = [...rows]; n[i] = { ...(n[i] || {}), [name]: v }; onChange(n); };
  const add = () => onChange([...rows, isString ? "" : {}]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="mt-1 space-y-2" data-testid={testid}>
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-start rounded-xl border border-primary/10 bg-card p-3">
          <div className="flex-1 space-y-2">
            {isString ? (
              <input value={row || ""} data-testid={`${testid}-${i}`} placeholder={itemLabel || "Item"}
                onChange={(e) => update(i, e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            ) : (
              item.map((sub) => (
                <div key={sub.name}>
                  <label className="text-xs text-muted-foreground">{sub.label}</label>
                  <input value={row?.[sub.name] ?? ""} data-testid={`${testid}-${i}-${sub.name}`}
                    onChange={(e) => updateField(i, sub.name, e.target.value)}
                    className="w-full mt-0.5 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                </div>
              ))
            )}
          </div>
          <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
            className="shrink-0 w-9 h-9 rounded-lg text-primary/50 hover:text-destructive hover:bg-destructive/10 flex items-center justify-center"><Trash2 size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testid}-add`}
        className="flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/20 transition-colors">
        <Plus size={15} /> {addLabel || "Add row"}
      </button>
    </div>
  );
}

// fields: [{ name, label, type: 'text'|'textarea'|'number'|'checkbox'|'image'|'html'|'json', item, itemLabel, addLabel, help }]
export default function CrudManager({ title, endpoint, fields, titleField, subField }) {
  const [items, setItems] = useState([]);
  const [libraryField, setLibraryField] = useState(null);
  const [editing, setEditing] = useState(null); // object or null
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const toArray = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") { try { return JSON.parse(v || "[]"); } catch { return []; } }
    return [];
  };

  const blank = () => {
    const o = {};
    fields.forEach((f) => { o[f.name] = f.type === "checkbox" ? false : f.type === "number" ? 0 : f.type === "json" ? [] : ""; });
    return o;
  };

  const openNew = () => { setEditing(blank()); setOpen(true); };
  const openEdit = (item) => {
    const o = { ...item };
    fields.forEach((f) => { if (f.type === "json") o[f.name] = toArray(o[f.name]); });
    setEditing(o); setOpen(true);
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...editing };
      fields.forEach((f) => {
        if (f.type === "json") payload[f.name] = toArray(payload[f.name]);
        if (f.type === "number") payload[f.name] = Number(payload[f.name]);
      });
      const id = payload.id;
      if (id) await api.put(`/admin/${endpoint}/${id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(err.message || formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid={`crud-${endpoint}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-medium text-primary">{title}</h1>
        <button onClick={openNew} data-testid={`crud-add-${endpoint}`} className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"><Plus size={16} /> Add</button>
      </div>

      <div className="bg-card border border-primary/10 rounded-2xl divide-y divide-primary/10 overflow-hidden">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-primary/5">
            {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-primary truncate">{item[titleField] || "Untitled"}</p>
              {subField && <p className="text-sm text-muted-foreground truncate">{item[subField]}</p>}
            </div>
            <button onClick={() => openEdit(item)} data-testid={`crud-edit-${item.id}`} className="p-2 text-primary/60 hover:text-secondary"><Pencil size={16} /></button>
            <button onClick={() => del(item.id)} data-testid={`crud-del-${item.id}`} className="p-2 text-primary/60 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-center text-muted-foreground">No items yet.</p>}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-[60] bg-primary/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div role="dialog" aria-modal="true" className="bg-background rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 no-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-2xl font-semibold text-primary">{editing.id ? "Edit" : "New"} {title}</h2>
              <button onClick={() => setOpen(false)}><X className="text-primary/60" /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  {f.type !== "focal_image" && <label className="text-sm font-medium text-primary">{f.label}</label>}
                  {f.type === "focal_image" ? (
                    <ImageEditor label={f.label} testid={`field-${f.name}`}
                      value={{ url: editing[f.name] || "", ...(editing[`${f.name}_focal`] || {}) }}
                      onChange={(v) => setEditing((prev) => ({
                        ...prev,
                        [f.name]: v.url,
                        [`${f.name}_focal`]: { pos_x: v.pos_x, pos_y: v.pos_y, zoom: v.zoom, rotate: v.rotate },
                      }))} />
                  ) : f.type === "image" ? (
                    <div className="mt-1">
                      <div className="flex items-center gap-3">
                        {editing[f.name] ? (
                          <img src={editing[f.name]} alt="" className="w-16 h-16 rounded-xl object-cover border border-primary/10" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl border border-dashed border-primary/20 flex items-center justify-center text-primary/30"><ImageIcon size={20} /></div>
                        )}
                        <button type="button" onClick={() => setLibraryField(f.name)} data-testid={`field-image-btn-${f.name}`} className="rounded-full bg-primary/10 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/20 flex items-center gap-2"><ImageIcon size={15} /> Choose / Upload</button>
                      </div>
                      <input data-testid={`field-${f.name}`} value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })} placeholder="or paste an image URL" className="w-full mt-2 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                    </div>
                  ) : f.type === "html" ? (
                    <div className="mt-1">
                      <RichEditor value={editing[f.name] ?? ""} testid={`field-${f.name}`}
                        onChange={(html) => setEditing((prev) => ({ ...prev, [f.name]: html }))} />
                    </div>
                  ) : f.type === "json" ? (
                    <RepeaterField value={editing[f.name]} item={f.item} itemLabel={f.itemLabel} addLabel={f.addLabel}
                      testid={`field-${f.name}`} onChange={(arr) => setEditing((prev) => ({ ...prev, [f.name]: arr }))} />
                  ) : f.type === "textarea" ? (
                    <textarea rows={3} data-testid={`field-${f.name}`} value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                  ) : f.type === "checkbox" ? (
                    <div className="mt-1"><input type="checkbox" data-testid={`field-${f.name}`} checked={!!editing[f.name]} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.checked })} className="w-5 h-5 accent-[hsl(var(--secondary))]" /></div>
                  ) : (
                    <input type={f.type === "number" ? "number" : "text"} data-testid={`field-${f.name}`} value={editing[f.name] ?? ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                  )}
                  {f.help && <p className="text-xs text-muted-foreground mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={loading} data-testid="crud-save" className="flex-1 rounded-full bg-primary text-primary-foreground py-3 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">{loading ? "Saving..." : "Save"}</button>
              <button onClick={() => setOpen(false)} className="rounded-full border border-primary/20 px-6 font-semibold text-primary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ImageLibrary open={!!libraryField} onClose={() => setLibraryField(null)} currentUrl={libraryField ? editing?.[libraryField] : ""}
        onSelect={(url) => { setEditing((prev) => ({ ...prev, [libraryField]: url })); setLibraryField(null); }} />
    </div>
  );
}
